/**
 * Property Management Utility
 * Handles property code persistence and validation
 */

// Clear cart when property changes
export const clearCartOnPropertyChange = (newPropertyCode) => {
  if (typeof window !== 'undefined') {
    try {
      const storedProperty = getStoredPropertyCode();
      
      // If property is different from stored property, clear the cart
      if (storedProperty && storedProperty !== newPropertyCode) {
        console.log('🔄 Property changed from', storedProperty, 'to', newPropertyCode, '- clearing cart');
        
        // Clear booking cart
        localStorage.removeItem('bookingCart');
        
        // Clear checkout items
        localStorage.removeItem('checkoutItems');
        
        console.log('✓ Cart cleared due to property change');
        return true;
      }
    } catch (e) {
      console.warn('Error clearing cart on property change:', e);
    }
  }
  return false;
};

// Save property code to localStorage
export const savePropertyCode = (code) => {
  if (code && typeof window !== 'undefined') {
    try {
      // Validate property code before saving
      if (code === 'null' || code === 'undefined' || code.trim() === '') {
        console.warn('Invalid property code, not saving:', code);
        return false;
      }
      
      // Check if property is different from current saved property
      const currentSaved = getStoredPropertyCode();
      
      // Clear cart if property is changing
      if (currentSaved && currentSaved !== code) {
        clearCartOnPropertyChange(code);
      }
      
      if (currentSaved === code) {
        console.log('Property already saved, skipping duplicate save:', code);
        return true;
      }
      
      localStorage.setItem('selectedProperty', JSON.stringify({
        code: code,
        description: code,
        timestamp: Date.now()
      }));
      console.log('✓ Property code saved to localStorage:', code);
      return true;
    } catch (e) {
      console.warn('Error saving property to localStorage:', e);
      return false;
    }
  }
  return false;
};

// Get property code from localStorage
export const getStoredPropertyCode = () => {
  if (typeof window !== 'undefined') {
    try {
      const savedProperty = localStorage.getItem('selectedProperty');
      if (savedProperty) {
        const property = JSON.parse(savedProperty);
        if (property && property.code) {
          console.log('Retrieved property code from localStorage:', property.code);
          return property.code;
        }
      }
    } catch (e) {
      console.warn('Error reading property from localStorage:', e);
    }
  }
  console.log('No stored property found, will use PMH as default');
  return null;
};

// Get property code from URL parameters
export const getPropertyFromURL = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('property') || urlParams.get('property_code');
  }
  return null;
};

// Ensure property is in URL parameters
export const ensurePropertyInURL = (propertyCode) => {
  if (typeof window !== 'undefined' && propertyCode) {
    const urlParams = new URLSearchParams(window.location.search);
    
    // If property is not in URL, add it
    if (!urlParams.has('property')) {
      urlParams.set('property', propertyCode);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
      console.log('Added property code to URL:', newUrl);
      return true;
    }
  }
  return false;
};

// Get property code with fallback logic
export const getPropertyCode = () => {
  // First try URL - URL always takes precedence
  let propertyCode = getPropertyFromURL();
  
  // Validate URL property
  if (propertyCode && (propertyCode === 'null' || propertyCode === 'undefined' || propertyCode.trim() === '')) {
    console.warn('⚠ Invalid property in URL detected:', propertyCode);
    
    // Try to get from localStorage
    const savedProperty = getStoredPropertyCode();
    
    // Use saved property or fallback to PMH
    const propertyToUse = savedProperty || 'PMH';
    
    if (savedProperty) {
      console.log('✓ Restoring property from localStorage:', savedProperty);
    } else {
      console.log('⚠ No saved property, using PMH fallback');
      // Save PMH to localStorage so it persists
      savePropertyCode('PMH');
    }
    
    propertyCode = propertyToUse;
    
    // Immediately fix the URL by replacing the invalid property
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('property', propertyToUse);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
      console.log('✓ URL fixed with valid property:', newUrl);
    }
  }
  
  // If not in URL or invalid, try localStorage
  if (!propertyCode) {
    propertyCode = getStoredPropertyCode();
    
    // If found in localStorage, add to URL
    if (propertyCode) {
      ensurePropertyInURL(propertyCode);
      console.log('✓ Restored property from localStorage to URL:', propertyCode);
    }
  } else {
    // If in URL and valid, save to localStorage if it's different
    const storedProperty = getStoredPropertyCode();
    if (!storedProperty || storedProperty !== propertyCode) {
      // Save the new property - this ensures localStorage is always up to date
      savePropertyCode(propertyCode);
      console.log('✓ Property from URL saved to localStorage:', propertyCode);
    }
  }
  
  return propertyCode;
};

// Validate if property code exists and is valid
export const validatePropertyCode = (propertyCode) => {
  // Basic validation - you can extend this with API calls
  if (!propertyCode || typeof propertyCode !== 'string') {
    return false;
  }
  
  // Check if it's a valid format (alphanumeric, 2-20 characters)
  const validFormat = /^[A-Za-z0-9]{2,20}$/.test(propertyCode);
  
  return validFormat;
};

// Ensure all required URL parameters are present
export const ensureRequiredParams = (router) => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    let hasChanges = false;
    
    // Get or set property
    let propertyCode = getPropertyCode();
    if (!propertyCode) {
      // Default to PMH when no property is found
      propertyCode = 'PMH';
      console.log('⚠ No property found, defaulting to PMH');
      savePropertyCode(propertyCode);
      urlParams.set('property', propertyCode);
      hasChanges = true;
    } else {
      // Even if property exists, ensure it's saved to localStorage
      savePropertyCode(propertyCode);
      console.log('✓ Property ensured in localStorage:', propertyCode);
    }
    
    // Ensure required booking parameters
    if (!urlParams.has('checkIn')) {
      const today = new Date();
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      urlParams.set('checkIn', formatDate(today));
      hasChanges = true;
    }
    
    if (!urlParams.has('checkOut')) {
      const checkIn = new Date(urlParams.get('checkIn') || new Date());
      const checkOut = new Date(checkIn);
      checkOut.setDate(checkIn.getDate() + 1);
      
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      urlParams.set('checkOut', formatDate(checkOut));
      hasChanges = true;
    }
    
    if (!urlParams.has('adults')) {
      urlParams.set('adults', '2');
      hasChanges = true;
    }
    
    if (!urlParams.has('children')) {
      urlParams.set('children', '0');
      hasChanges = true;
    }
    
    if (!urlParams.has('room')) {
      urlParams.set('room', '1');
      hasChanges = true;
    }
    
    // Update URL if changes were made
    if (hasChanges) {
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      if (router) {
        router.replace(newUrl);
      } else {
        window.history.replaceState({}, '', newUrl);
      }
      console.log('Updated URL with required parameters:', newUrl);
    }
    
    return propertyCode;
  }
  
  // Default to PMH as final fallback
  console.log('⚠ ensureRequiredParams: Using PMH as final fallback');
  return 'PMH';
};
